/* Portland showroom page: live open/closed status, product context from a product page referrer,
   the note form's tracking, and a scoped release of the Klaviyo popup scroll lock.
   The note form is a native Shopify contact form; tracking fires only on its success render. */
(() => {
  'use strict';
  const root = document.querySelector('[data-wnfv-page]');
  if (!root || root.dataset.wnfvReady) return;
  root.dataset.wnfvReady = 'true';

  const measurementId = 'G-0JE425EEKS';
  const pendingKey = 'wnfv-visit-note';
  const maxPendingMs = 30 * 60 * 1000;

  /* 1. Live open/closed status, in Portland time. */
  const HOURS = { 0: [11, 17], 1: [10, 18], 2: [10, 18], 3: [10, 18], 4: [10, 18], 5: [10, 18], 6: [10, 18] };
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const pdxNow = () => {
    try { return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })); } catch { return new Date(); }
  };
  const fmt = h => `${h % 12 || 12}${h >= 12 ? 'pm' : 'am'}`;
  function status() {
    const now = pdxNow();
    const day = now.getDay();
    const mins = now.getHours() * 60 + now.getMinutes();
    const [open, close] = HOURS[day];
    if (mins >= open * 60 && mins < close * 60) return { open: true, day, pill: `Open now · closes ${fmt(close)}`, strong: 'Open now', detail: `Closes ${fmt(close)} today` };
    if (mins < open * 60) return { open: false, day, pill: `Opens ${fmt(open)} today`, strong: 'Closed now', detail: `Opens today at ${fmt(open)}` };
    const next = (day + 1) % 7;
    return { open: false, day, pill: `Closed · opens ${fmt(HOURS[next][0])} tomorrow`, strong: 'Closed now', detail: `Opens tomorrow (${DAYS[next]}) at ${fmt(HOURS[next][0])}` };
  }
  function applyStatus() {
    const s = status();
    root.querySelectorAll('[data-vs-status], [data-vs-bigstatus]').forEach(el => {
      el.classList.toggle('is-open', s.open);
      el.classList.toggle('is-closed', !s.open);
    });
    const pill = root.querySelector('[data-vs-status-txt]');
    const strong = root.querySelector('[data-vs-status-strong]');
    const detail = root.querySelector('[data-vs-status-detail]');
    if (pill) pill.textContent = s.pill;
    if (strong) strong.textContent = s.strong;
    if (detail) detail.textContent = s.detail;
    root.querySelectorAll('[data-vs-hours] li').forEach(li => li.classList.toggle('is-today', Number(li.dataset.day) === s.day));
  }
  applyStatus();
  window.setInterval(applyStatus, 60000);

  /* 2. Product context: a visitor arriving from a product page sees its name and the note is prefilled. */
  const note = root.querySelector('[data-vs-interest]');
  const productField = root.querySelector('[data-vs-product-field]');
  const personal = root.querySelector('[data-vs-personal]');
  function handleFromReferrer() {
    try {
      const ref = new URL(document.referrer);
      if (ref.hostname.replace(/^www\./, '') !== location.hostname.replace(/^www\./, '')) return null;
      const match = ref.pathname.match(/\/products\/([a-z0-9-]+)/i);
      return match ? match[1] : null;
    } catch { return null; }
  }
  function showProduct(name) {
    name = String(name || '').replace(/\s+/g, ' ').trim().slice(0, 120);
    if (!name) return;
    if (personal) {
      personal.textContent = `Looking at the ${name}? `;
      const link = document.createElement('a');
      link.href = '#rsvp';
      link.textContent = 'Tell us before you come';
      personal.append(link, ' and we’ll check whether it’s on the floor.');
      personal.hidden = false;
    }
    if (note && !note.value) note.value = `I’d like to see the ${name}.`;
    if (productField) productField.value = name;
  }
  const params = new URLSearchParams(location.search);
  const named = params.get('product') || params.get('from');
  const handle = params.get('product_handle') || handleFromReferrer();
  if (named) showProduct(named);
  else if (handle && !root.querySelector('[data-vs-done]')) {
    fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : null))
      .then(p => { if (p && p.title) showProduct(p.title); })
      .catch(() => {});
  }

  /* 3. Note form. Values are kept in this tab only until Shopify confirms the message was sent. */
  const storage = {
    get() { try { return JSON.parse(sessionStorage.getItem(pendingKey) || 'null'); } catch { return null; } },
    set(v) { try { sessionStorage.setItem(pendingKey, JSON.stringify(v)); } catch { /* The native form works without storage. */ } },
    clear() { try { sessionStorage.removeItem(pendingKey); } catch { /* No storage available. */ } }
  };
  const analyticsAllowed = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  function ensurePrivacyApi() {
    if (window.Shopify?.customerPrivacy || typeof window.Shopify?.loadFeatures !== 'function') return;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
  }

  const section = root.querySelector('#rsvp');
  const form = section?.querySelector('form');
  const success = section?.querySelector('[data-vs-done]');
  const serverError = section?.querySelector('[data-vs-error]');
  const statusBox = success || serverError;
  if (statusBox) {
    const focusStatus = () => statusBox.focus({ preventScroll: true });
    focusStatus();
    if (document.readyState !== 'complete') window.addEventListener('load', () => window.setTimeout(focusStatus, 0), { once: true });
  }

  if (success) {
    const pending = storage.get();
    storage.clear();
    if (pending && Date.now() - pending.t < maxPendingMs) {
      const interest = pending.interest || pending.product || '';
      try {
        const kl = window.klaviyo || (window._klOnsite = window._klOnsite || []);
        const [first, ...rest] = pending.name.split(' ');
        kl.push(['identify', { $email: pending.email, $first_name: first, $last_name: rest.join(' '), $phone_number: pending.phone || undefined, 'Showroom Visit Intent': true, 'Showroom Visit Interest': interest }]);
        kl.push(['track', 'Planned Showroom Visit', { Interest: interest, Timeframe: pending.when, Showroom: 'Portland', Source: 'visit-portland-page', Page: location.pathname }]);
      } catch { /* Klaviyo is optional; the message already reached contact@. */ }
      try { (window.dataLayer = window.dataLayer || []).push({ event: 'plan_visit_lead', visit_timeframe: pending.when, visit_showroom: 'Portland' }); } catch { /* optional */ }
      ensurePrivacyApi();
      let attempts = 0;
      const reportLead = () => {
        const privacyReady = typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function';
        if (privacyReady && !analyticsAllowed()) return;
        if (privacyReady && typeof window.gtag === 'function') {
          window.gtag('event', 'generate_lead', { send_to: measurementId, lead_type: 'plan_visit', value: 1, form_name: 'showroom_visit' });
          return;
        }
        if (++attempts < 30) window.setTimeout(reportLead, 500);
      };
      reportLead();
    }
  } else if (serverError) {
    storage.clear();
  }

  if (form && note) {
    // With JavaScript the note is optional; Shopify still needs a message body, so a summary fills an empty one.
    note.required = false;
    const optional = form.querySelector('[data-vs-optional]');
    if (optional) optional.hidden = false;
    form.addEventListener('submit', () => {
      const get = n => (form.querySelector(`[name="${n}"]`)?.value || '').trim();
      const when = get('contact[Visit timing]');
      const product = get('contact[Product]');
      if (!note.value.trim()) note.value = `Planning a visit to the Portland showroom (${when || 'Not sure yet'}). No extra notes.`;
      storage.set({ name: get('contact[name]'), email: get('contact[email]'), phone: get('contact[phone]'), when, product, interest: product || get('contact[body]').slice(0, 200), t: Date.now() });
    });
  }

  /* 4. The Klaviyo popup is hidden on this page by CSS. Release only Klaviyo's own body scroll lock if it still applies it. */
  const lockClass = 'klaviyo-prevent-body-scrolling';
  const release = () => { if (document.body.classList.contains(lockClass)) document.body.classList.remove(lockClass); };
  release();
  new MutationObserver(release).observe(document.body, { attributes: true, attributeFilter: ['class'] });
})();
